import { Color } from '@persona-id/design-tokens';
import { MergeIcon } from '@persona-id/design-tokens/icons';
import { noop } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import Permissioned from 'dashboard/components/Permissioned';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import {
  FieldSchemaEditorTable,
  getAllFieldKeys,
} from 'dashboard/components-lib/FieldSchemaEditor';
import { FieldSchemaEditorForm } from 'dashboard/components-lib/FieldSchemaEditorForm';
import {
  Backdrop,
  FULL_SCREEN_HEADER_HEIGHT,
  FullScreenHeaderContainer,
} from 'dashboard/layouts/ShowViewLayoutV2';
import { TransactionType } from 'dashboard/models/transaction-type';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { Span } from 'design-system/components/Text';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

const RIGHT_COLUMN_PORTAL_ID = 'txn-inactive-fields-portal';

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  height: calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT}px);
`;

interface DashboardTransactionInactiveFieldsViewProps {
  transactionType: TransactionType;
  handleActivateField: (fieldSchemaKey: string) => void;
}

const DashboardTransactionInactiveFieldsView = ({
  transactionType,
  handleActivateField,
}: DashboardTransactionInactiveFieldsViewProps) => {
  const [activateFieldModal, { enable: showActivateFieldModal, disable: hideActivateFieldModal }] =
    useToggle(false);

  const [activeFieldKey, setActiveFieldKey] = useState<string | null>(null);

  const inactiveFieldSchemas = useMemo(
    () =>
      transactionType?.attributes?.['field-schemas'].filter(
        ({ config: { 'deactivated-at': deactivatedAt } }) => deactivatedAt != null,
      ) || [],
    [transactionType],
  );

  const enableFieldSchemaEditorForm = useFeatureFlag('dashboard:enable-field-schema-editor-form');

  return (
    <>
      <ModalWrapper isOpen={activateFieldModal} handleCloseModal={hideActivateFieldModal}>
        <ConfirmationModal
          cancelAction={hideActivateFieldModal}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          confirmAction={() => handleActivateField(activeFieldKey)}
          header='Are you sure you want to activate this field?'
          confirmActionText='Activate Field'
        >
          Activating a field that is used in Workflows or elsewhere within Persona may cause errors.
        </ConfirmationModal>
      </ModalWrapper>
      <FeatureFlagged featureFlag='dashboard:txn-layout-editor' fallback={null} invert>
        <FullScreenHeaderContainer>
          <HorizontalContainer>
            <NavBack to={`/dashboard/transaction-types/${transactionType.id}`}>
              {transactionType.attributes.name} Inactive Fields
            </NavBack>
          </HorizontalContainer>
        </FullScreenHeaderContainer>
      </FeatureFlagged>
      <Container>
        <Backdrop>
          {enableFieldSchemaEditorForm ? (
            <FieldSchemaEditorForm
              fieldSchemas={inactiveFieldSchemas}
              nullState={<Span>Looks like there aren&apos;t any inactive fields yet.</Span>}
              canDeactivate={() => false}
              canDelete={() => false}
              canEdit={() => false}
              additionalColumns={[
                {
                  header: 'Date deactivated (UTC)',
                  generateContent: ({ field, prefix }) => {
                    // Shorthand to detect the field is a top level field
                    if (field.key === prefix) {
                      return DateTime.fromISO(field.config['deactivated-at'])
                        .toUTC()
                        .toFormat(DATE_AND_TIME_FORMAT);
                    }
                  },
                },
                {
                  header: '' /* activate button */,
                  width: '100px',
                  generateContent: ({ field, prefix }) => {
                    // Shorthand to detect the field is a top level field
                    if (field.key === prefix) {
                      return (
                        <Permissioned permission={Permission.TransactionTypeWrite}>
                          <div style={{ marginLeft: 'auto' }}>
                            <Button
                              width='auto'
                              variant='danger'
                              id={`transaction-type-inactive-fields-show__activate-button__row--${field.key}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                showActivateFieldModal();
                                setActiveFieldKey(field.key);
                              }}
                            >
                              Activate
                            </Button>
                          </div>
                        </Permissioned>
                      );
                    }
                  },
                },
              ]}
              rightPanelPortalId={RIGHT_COLUMN_PORTAL_ID}
              editable={false}
              onSubmit={noop}
              showActive={false}
              showInactive={true}
            />
          ) : (
            <Card>
              <Card.Body>
                <Card.Title>Inactive Fields</Card.Title>
                <FieldSchemaEditorTable
                  productIcon={<MergeIcon size={48} color={Color.BorderIconSecondary} />}
                  inputSchema={inactiveFieldSchemas}
                  uneditableKeys={getAllFieldKeys(inactiveFieldSchemas)}
                  erroredKeys={[]}
                  editable={false}
                  customColumns={[
                    {
                      header: 'Date deactivated (UTC)',
                      generateContent: ({ field, prefix }) => {
                        // Shorthand to detect the field is a top level field
                        if (field.key === prefix) {
                          return DateTime.fromISO(field.config['deactivated-at'])
                            .toUTC()
                            .toFormat(DATE_AND_TIME_FORMAT);
                        }
                      },
                    },
                    {
                      header: '' /* activate button */,
                      width: '100px',
                      generateContent: ({ field, prefix }) => {
                        // Shorthand to detect the field is a top level field
                        if (field.key === prefix) {
                          return (
                            <Permissioned permission={Permission.TransactionTypeWrite}>
                              <div style={{ marginLeft: 'auto' }}>
                                <Button
                                  width='auto'
                                  variant='danger'
                                  id={`transaction-type-inactive-fields-show__activate-button__row--${field.key}`}
                                  onClick={() => {
                                    showActivateFieldModal();
                                    setActiveFieldKey(field.key);
                                  }}
                                >
                                  Activate
                                </Button>
                              </div>
                            </Permissioned>
                          );
                        }
                      },
                    },
                  ]}
                  onUpdateSchema={() => {
                    return;
                  }}
                  onFocusSchema={() => {
                    return;
                  }}
                  nullState={
                    <Permissioned
                      permission={Permission.TransactionTypeView}
                      fallback={
                        <div>
                          User permission required to view transaction type details. Please contact
                          your account admin if you&#39;d like to view transaction types.
                        </div>
                      }
                    >
                      <h3>Looks like there aren&#39;t any inactive fields yet.</h3>
                    </Permissioned>
                  }
                />
              </Card.Body>
            </Card>
          )}
        </Backdrop>
        <div id={RIGHT_COLUMN_PORTAL_ID} />
      </Container>
    </>
  );
};

export default DashboardTransactionInactiveFieldsView;
